import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, AlertTriangle } from "lucide-react";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getCondition, getHub, conditionsForHub } from "@/data/care";
import { medicalWebPageJsonLd, faqPageJsonLd, breadcrumbListJsonLd } from "@/data/schema";
import { WHATSAPP_LINK } from "@/lib/api";

const Section = ({ n, title, children }) => (
  <FadeUp>
    <div className="grid grid-cols-12 gap-6 md:gap-10 py-10 border-t border-black/10">
      <div className="col-span-12 md:col-span-3">
        <div className="kbd">— {n}</div>
        <h3 className="mt-2 font-serif text-2xl md:text-3xl tracking-tightest">{title}</h3>
      </div>
      <div className="col-span-12 md:col-span-9">{children}</div>
    </div>
  </FadeUp>
);

export default function Condition() {
  const { slug } = useParams();
  const c = getCondition(slug);
  if (!c) return <Navigate to="/care" replace />;
  const hub = getHub(c.hub);
  const related = conditionsForHub(c.hub).filter((x) => x.slug !== c.slug).slice(0, 4);

  return (
    <div data-testid={`condition-page-${c.slug}`} className="pt-28 md:pt-36 pb-28">
      <SEO
        title={`${c.name} — ${hub.name} · Dr Tegbir Vascular, Mohali`}
        description={`${c.oneLiner} ${c.whatIsIt}`.slice(0, 240)}
        keywords={[c.name.toLowerCase(), `${c.name.toLowerCase()} treatment mohali`, `${c.name.toLowerCase()} interventional radiology`, hub.name.toLowerCase()]}
      />
      <JsonLd data={[
        medicalWebPageJsonLd({ name: c.name, description: `${c.oneLiner} ${c.whatIsIt}`, url: `/conditions/${c.slug}`, about: c.name }),
        faqPageJsonLd(c.faqs),
        breadcrumbListJsonLd([
          { to: "/", label: "Home" },
          { to: "/care", label: "Care" },
          { to: `/care/${hub.slug}`, label: hub.name },
          { label: c.name },
        ]),
      ]}/>
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <FadeUp>
          <Breadcrumbs trail={[{ to: "/", label: "Home" }, { to: "/care", label: "Care" }, { to: `/care/${hub.slug}`, label: hub.name }, { label: c.name }]} />
        </FadeUp>

        {/* Hero */}
        <div className="mt-12 grid grid-cols-12 gap-6">
          <FadeUp className="col-span-12 md:col-span-8">
            <div className="kbd">{hub.kicker} · Condition</div>
            <h1 className="mt-6 font-serif text-5xl md:text-[7vw] leading-[0.96] tracking-tightest">
              <MaskedReveal text={c.name} stagger={0.06}/>
            </h1>
            <p className="mt-10 text-xl md:text-2xl font-serif italic leading-snug max-w-2xl">"{c.oneLiner}"</p>
          </FadeUp>
          <FadeUp className="col-span-12 md:col-span-4 md:pl-8" delay={0.2}>
            <div className="hairline p-6 bg-white/60">
              <div className="kbd">Speak to the doctor</div>
              <p className="mt-3 text-sm text-ink-muted leading-relaxed">
                Every case is different. Get a personal review by Dr Sidhu — not a template response.
              </p>
              <div className="mt-6 flex flex-col gap-2">
                <Link to="/contact" data-testid="condition-cta-book" className="btn-pill-inv justify-center">
                  Book consultation <ArrowUpRight size={14}/>
                </Link>
                <Link to={`/second-opinion?condition=${c.slug}`} data-testid="condition-cta-second-opinion" className="btn-pill justify-center">
                  Second opinion in 48h
                </Link>
                <a target="_blank" rel="noreferrer" href={WHATSAPP_LINK(`Hello Dr Sidhu, I would like to enquire about ${c.name}.`)} data-testid="condition-cta-whatsapp" className="btn-pill justify-center">
                  WhatsApp
                </a>
              </div>
            </div>
          </FadeUp>
        </div>

        {/* Urgent banner (conditional) */}
        {c.whenUrgent && (
          <FadeUp className="mt-16">
            <div className="hairline bg-crimson-soft p-5 md:p-6 flex items-start gap-3">
              <AlertTriangle size={18} className="text-crimson flex-none mt-1"/>
              <div>
                <div className="kbd text-crimson">— Time-sensitive</div>
                <p className="mt-2 text-ink leading-relaxed">{c.whenUrgent}</p>
                <Link to="/urgent" data-testid="condition-urgent-link" className="mt-3 inline-flex items-center gap-2 text-sm text-crimson underline">
                  Urgent evaluation path <ArrowUpRight size={12}/>
                </Link>
              </div>
            </div>
          </FadeUp>
        )}

        {/* Body */}
        <div className="mt-16">
          <Section n="01" title="What it is">
            <p className="text-lg text-ink leading-relaxed max-w-3xl">{c.whatIsIt}</p>
          </Section>

          {c.symptoms && c.symptoms.length > 0 && (
            <Section n="02" title="Symptoms & signs">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-8 max-w-3xl">
                {c.symptoms.map((s, i) => (
                  <li key={i} className="text-ink-muted leading-relaxed flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-crimson flex-none" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section n="03" title="Diagnosis">
            <p className="text-lg text-ink leading-relaxed max-w-3xl">{c.diagnosis}</p>
          </Section>

          <Section n="04" title="Treatment">
            <p className="text-lg text-ink leading-relaxed max-w-3xl">{c.treatment}</p>
          </Section>

          {c.faqs && c.faqs.length > 0 && (
            <Section n="05" title="Frequently asked">
              <Accordion type="single" collapsible data-testid={`condition-faqs-${c.slug}`}>
                {c.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-b border-black/10">
                    <AccordionTrigger className="text-left text-lg font-serif tracking-tightest py-5">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-ink-muted leading-relaxed pb-5">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Section>
          )}
        </div>

        {/* Disclaimer */}
        <FadeUp className="mt-16">
          <div className="hairline p-5 md:p-6 bg-white/60 kbd text-ink-muted">
            The information above is for general education and does not replace personal medical advice. For a case-specific view, book a consultation with Dr Sidhu.
          </div>
        </FadeUp>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-24">
            <FadeUp>
              <div className="hairline-t pt-8">
                <div className="kbd">— Related conditions in {hub.name}</div>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {related.map((r) => (
                    <Link key={r.slug} to={`/conditions/${r.slug}`} data-testid={`related-${r.slug}`} className="group p-5 hairline hover:bg-white transition-colors">
                      <div className="font-serif text-xl tracking-tightest group-hover:text-crimson">{r.name}</div>
                      <div className="mt-2 text-xs text-ink-muted">{r.oneLiner}</div>
                    </Link>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>
        )}
      </div>
    </div>
  );
}
